import * as R from 'ramda';

import {
  VegaAggregateOp,
  VegaAggregateTransform,
  VegaCalculateTransform,
  VegaEncodingChannel,
  VegaFilterTransform,
  VegaTransform,
  VegaWindowTransform,
} from '@atom/types/conversationalAnalytics';

type FormatTimeDataResult = {
  data: Record<string, unknown>[];
  displayField: string;
};

export const CHART_MARGINS = { top: 14, right: 14, left: 14, bottom: 24 };
export const CHART_HEIGHT = 400;

export const CHART_COLORS = [
  '#00BCD4',
  '#509DE9',
  '#999CFF',
  '#F690B2',
  '#E354B4',
  '#4A18BD',
  '#1D905B',
  '#7BD148',
  '#FBC02D',
  '#AC715D',
  '#F93922',
  '#513838',
];

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const getFieldName = (channel?: VegaEncodingChannel): string | null => {
  if (!channel || !channel.field) {
    return null;
  }

  return channel.field;
};

export const getDisplayFieldName = (
  channel?: VegaEncodingChannel,
): string | null => {
  if (!channel || !channel.field) {
    return null;
  }

  if (channel.aggregate) {
    return `${channel.aggregate}_${channel.field}`;
  }

  return channel.field;
};

export const getChannelTitle = (
  channel?: VegaEncodingChannel,
): string | undefined => {
  return channel?.title || channel?.axis?.title || channel?.field;
};

export const formatTimeData = (
  data: Record<string, unknown>[],
  field: string,
  timeUnit?: string,
): FormatTimeDataResult => {
  if (!timeUnit) {
    return { data, displayField: field };
  }

  const displayField = `${field}_display`;

  const formattedData = data.map(item => {
    const value = item[field];

    if (typeof value !== 'number') {
      return R.assoc(displayField, value, item);
    }

    const date = new Date(value * 1000);

    if (timeUnit === 'month') {
      return R.assoc(displayField, MONTH_NAMES[date.getMonth()], item);
    }

    if (timeUnit === 'year') {
      return R.assoc(displayField, date.getFullYear().toString(), item);
    }

    if (timeUnit === 'date' || timeUnit === 'day') {
      return R.assoc(displayField, date.toLocaleDateString(), item);
    }

    return R.assoc(displayField, date.toLocaleString(), item);
  });

  return { data: formattedData, displayField };
};

const isAggregateTransform = (
  transform: VegaTransform,
): transform is VegaAggregateTransform => {
  return 'aggregate' in transform;
};

const isWindowTransform = (
  transform: VegaTransform,
): transform is VegaWindowTransform => {
  return 'window' in transform;
};

const isCalculateTransform = (
  transform: VegaTransform,
): transform is VegaCalculateTransform => {
  return 'calculate' in transform;
};

const isFilterTransform = (
  transform: VegaTransform,
): transform is VegaFilterTransform => {
  return 'filter' in transform;
};

const aggregateOperations: Record<
  VegaAggregateOp,
  (values: number[]) => number
> = {
  sum: values => R.sum(values),
  mean: values => R.mean(values),
  average: values => R.mean(values),
  min: values => Math.min(...values),
  max: values => Math.max(...values),
  median: values => R.median(values),
  count: values => values.length,
};

export const applyAggregateTransform = (
  data: Record<string, unknown>[],
  transform: VegaAggregateTransform,
): Record<string, unknown>[] => {
  const { aggregate, groupby = [] } = transform;

  if (groupby.length === 0) {
    const result: Record<string, unknown> = {};

    aggregate.forEach(({ op, field, as }) => {
      if (op === 'count') {
        result[as] = data.filter(item => item[field] !== null).length;
        return;
      }

      const values = data
        .map(item => item[field])
        .filter(val => typeof val === 'number') as number[];

      result[as] = aggregateOperations[op](values);
    });

    return [result];
  }

  const grouped = R.groupBy(
    item => groupby.map(field => item[field]).join('|'),
    data,
  );

  return Object.values(grouped).map(group => {
    const result: Record<string, unknown> = {};

    groupby.forEach(field => {
      result[field] = group[0][field];
    });

    aggregate.forEach(({ op, field, as }) => {
      if (op === 'count') {
        result[as] = group.filter(item => item[field] !== null).length;
        return;
      }

      const values = group
        .map(item => item[field])
        .filter(val => typeof val === 'number') as number[];

      result[as] = aggregateOperations[op](values);
    });

    return result;
  });
};

export const applyWindowTransform = (
  data: Record<string, unknown>[],
  transform: VegaWindowTransform,
): Record<string, unknown>[] => {
  const { window, sort } = transform;

  let processedData = [...data];

  if (sort && sort.length > 0) {
    const sortFields = sort.map(sortSpec => sortSpec.field);
    const sortOrders = sort.map(sortSpec =>
      sortSpec.order === 'descending' ? 'desc' : 'asc',
    );
    processedData = R.sortWith(
      sortFields.map((field, index) =>
        sortOrders[index] === 'desc'
          ? R.descend(R.prop(field))
          : R.ascend(R.prop(field)),
      ),
      processedData,
    );
  }

  window.forEach(({ op, as }) => {
    if (op === 'rank') {
      processedData = processedData.map((item, index) => ({
        ...item,
        [as]: index + 1,
      }));
    }
  });

  return processedData;
};

const evaluateCalculateExpression = (
  expression: string,
  datum: Record<string, unknown>,
): unknown => {
  const expr = expression.replace(/datum\.(\w+)/g, (_, field) => {
    const value = datum[field];
    if (typeof value === 'string') {
      return `"${value}"`;
    }
    return String(value ?? 'null');
  });

  try {
    // eslint-disable-next-line no-eval -- required to evaluate vega-lite calculate expressions
    return eval(expr);
  } catch {
    return null;
  }
};

export const applyFilterTransform = (
  data: Record<string, unknown>[],
  transform: VegaFilterTransform,
): Record<string, unknown>[] => {
  const { filter } = transform;

  if (typeof filter !== 'string') {
    return data;
  }

  return data.filter(datum => {
    const expr = filter.replace(/datum\.(\w+)/g, (_, field) => {
      const value = datum[field];

      if (typeof value === 'string') {
        return `"${value}"`;
      }

      return String(value ?? 'null');
    });

    try {
      // eslint-disable-next-line no-eval -- required to evaluate vega-lite filter expressions
      return Boolean(eval(expr));
    } catch {
      return true;
    }
  });
};

export const applyCalculateTransform = (
  data: Record<string, unknown>[],
  transform: VegaCalculateTransform,
): Record<string, unknown>[] => {
  const { calculate, as } = transform;

  return data.map(item => ({
    ...item,
    [as]: evaluateCalculateExpression(calculate, item),
  }));
};

export const applyTransforms = (
  data: Record<string, unknown>[],
  transforms?: VegaTransform[],
): Record<string, unknown>[] => {
  if (!transforms || transforms.length === 0) {
    return data;
  }

  let processedData = data;

  transforms.forEach(transform => {
    if (isAggregateTransform(transform)) {
      processedData = applyAggregateTransform(processedData, transform);
    } else if (isWindowTransform(transform)) {
      processedData = applyWindowTransform(processedData, transform);
    } else if (isCalculateTransform(transform)) {
      processedData = applyCalculateTransform(processedData, transform);
    } else if (isFilterTransform(transform)) {
      processedData = applyFilterTransform(processedData, transform);
    }
  });

  return processedData;
};
